import { supabase } from './supabaseClient';
import { SHIPPED_APP_RELEASES, compareAppVersions } from '../constants/appRelease';

export const APP_VERSION_FLAVORS = [
  {
    id: 'customer',
    label: 'Customer app',
    hint: 'RoadServe (Play / App Store)',
  },
  {
    id: 'mechanic',
    label: 'Partner app',
    hint: 'RoadServe Partner (Play / App Store)',
  },
  {
    id: 'staff',
    label: 'Staff app',
    hint: 'RoadServe Team (internal APK)',
  },
];

export const emptyPolicy = (flavor) => ({
  flavor,
  latest_version: '',
  min_version: '',
  latest_build: '',
  min_build: '',
  force_update: false,
  prompt_update: false,
  update_title: 'Update required',
  update_message: 'A new version is available. Please update from the store to continue.',
  play_store_url: '',
  app_store_url: '',
  updated_at: null,
  updated_by: null,
});

export async function listAppVersionPolicies() {
  const { data, error } = await supabase
    .from('app_version_policies')
    .select('*')
    .order('flavor');
  if (error) throw error;
  return Array.isArray(data) ? data : [];
}

export async function saveAppVersionPolicy(row, updatedBy) {
  const payload = {
    flavor: row.flavor,
    latest_version: String(row.latest_version || '').trim() || '1.0.0',
    min_version: String(row.min_version || row.latest_version || '').trim() || '1.0.0',
    latest_build: row.latest_build === '' || row.latest_build == null ? null : Number(row.latest_build),
    min_build: row.min_build === '' || row.min_build == null ? null : Number(row.min_build),
    force_update: Boolean(row.force_update),
    prompt_update: Boolean(row.prompt_update),
    update_title: String(row.update_title || '').trim() || null,
    update_message: String(row.update_message || '').trim() || null,
    play_store_url: String(row.play_store_url || '').trim() || null,
    app_store_url: String(row.app_store_url || '').trim() || null,
    updated_at: new Date().toISOString(),
    updated_by: updatedBy || null,
  };
  const { error } = await supabase.from('app_version_policies').upsert(payload, { onConflict: 'flavor' });
  if (error) throw error;
  return payload;
}

export function normalizeAppFlavor(flavor) {
  const x = String(flavor || '').trim().toLowerCase();
  if (x === 'partner' || x === 'mechanic') return 'mechanic';
  if (x === 'customer' || x === 'rsa') return 'customer';
  if (x === 'staff' || x === 'team') return 'staff';
  return x;
}

function foldLiveRows(rows) {
  const byFlavor = {
    customer: { version: '', build: '', users: 0, lastSeen: null, lines: [] },
    mechanic: { version: '', build: '', users: 0, lastSeen: null, lines: [] },
    staff: { version: '', build: '', users: 0, lastSeen: null, lines: [] },
  };

  for (const row of rows || []) {
    const flavor = normalizeAppFlavor(row.flavor || row.app_flavor);
    if (!byFlavor[flavor]) continue;
    const version = String(row.app_version || '').trim();
    if (!version) continue;
    const build = String(row.app_build_number || '').trim();
    const count = Number(row.users_count || row.count || 1);
    const lastSeen = row.last_seen || row.app_version_reported_at || null;
    const platform = String(row.app_platform || row.platform || 'unknown');
    byFlavor[flavor].lines.push({ version, build, platform, count, lastSeen });
    byFlavor[flavor].users += count;
    if (lastSeen && (!byFlavor[flavor].lastSeen || new Date(lastSeen) > new Date(byFlavor[flavor].lastSeen))) {
      byFlavor[flavor].lastSeen = lastSeen;
    }
    const better =
      compareAppVersions(version, byFlavor[flavor].version) > 0 ||
      (compareAppVersions(version, byFlavor[flavor].version) === 0 &&
        (parseInt(build, 10) || 0) > (parseInt(byFlavor[flavor].build, 10) || 0));
    if (!byFlavor[flavor].version || better) {
      byFlavor[flavor].version = version;
      byFlavor[flavor].build = build;
    }
  }

  return byFlavor;
}

export async function fetchLiveAppVersions() {
  const usable = (folded) =>
    folded.customer.users || folded.mechanic.users || folded.staff.users;

  const { data, error } = await supabase.rpc('admin_live_app_versions');
  if (!error && Array.isArray(data) && data.length) {
    const folded = foldLiveRows(data);
    if (usable(folded)) return folded;
  }

  const { data: users, error: userErr } = await supabase
    .from('users')
    .select('app_version, app_build_number, app_flavor, app_platform, app_version_reported_at')
    .not('app_version', 'is', null)
    .limit(8000);
  if (!userErr && Array.isArray(users) && users.length) {
    const folded = foldLiveRows(users);
    if (usable(folded)) return folded;
  }

  const { data: all } = await supabase.rpc('admin_get_all_users_all');
  if (Array.isArray(all) && all.length) {
    return foldLiveRows(
      all
        .filter((u) => u.app_version)
        .map((u) => ({
          flavor: u.app_flavor,
          app_version: u.app_version,
          app_build_number: u.app_build_number,
          app_platform: u.app_platform,
          users_count: 1,
          last_seen: u.app_version_reported_at,
        })),
    );
  }

  return foldLiveRows([]);
}

export function autoVersionForFlavor(flavor, live) {
  const shipped = SHIPPED_APP_RELEASES[flavor] || { version: '', build: null };
  const seen = live?.[flavor] || { version: '', build: '', users: 0, lastSeen: null, lines: [] };
  let version = shipped.version || '';
  let build = shipped.build == null ? '' : String(shipped.build);
  if (
    seen.version &&
    (compareAppVersions(seen.version, version) > 0 ||
      (compareAppVersions(seen.version, version) === 0 &&
        (parseInt(seen.build, 10) || 0) > (parseInt(build, 10) || 0)))
  ) {
    version = seen.version;
    build = seen.build || build;
  }
  return {
    version,
    build,
    shipped,
    live: seen,
  };
}
